import React, { useState } from 'react';
import {
  PlayCircle,
  FileSearch,
  GitCompare,
  Download,
  RefreshCw,
  MoreHorizontal,
  AlertTriangle,
} from 'lucide-react';
import { ContractStatus } from '../../lib/contract-status';

export interface ContractActionsProps {
  contractStatus: ContractStatus;
  onProcess?: () => void;
  onAnalyze?: () => void;
  onCompare?: () => void;
  onExport?: () => void;
  onRefresh?: () => void;
  isProcessing?: boolean;
  isAnalyzing?: boolean;
}

const ActionButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'success';
  isLoading?: boolean;
  testId: string;
}> = ({
  icon,
  label,
  onClick,
  disabled,
  variant = 'secondary',
  isLoading,
  testId,
}) => {
  const baseClasses =
    'inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

  const variantClasses = {
    primary:
      'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 disabled:bg-primary-300',
    secondary:
      'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500 disabled:bg-gray-100 disabled:text-gray-400',
    success:
      'bg-success-600 text-white hover:bg-success-700 focus:ring-success-500 disabled:bg-success-300',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`${baseClasses} ${variantClasses[variant]}`}
      data-testid={testId}
    >
      {isLoading ? (
        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <span className="mr-2">{icon}</span>
      )}
      {label}
    </button>
  );
};

export const ContractActions: React.FC<ContractActionsProps> = ({
  contractStatus,
  onProcess,
  onAnalyze,
  onCompare,
  onExport,
  onRefresh,
  isProcessing,
  isAnalyzing,
}) => {
  const [showMore, setShowMore] = useState(false);

  // Determine which actions are available based on contract status
  const canProcess =
    contractStatus === 'uploaded' || contractStatus === 'error';
  const canAnalyze = ['structured', 'embedded', 'linked', 'ready'].includes(
    contractStatus
  );
  const canCompare = ['structured', 'embedded', 'linked', 'ready'].includes(
    contractStatus
  );
  const canExport = contractStatus === 'ready';

  // Status-based messaging
  const getStatusMessage = (): { message: string; type: 'info' | 'warning' } | null => {
    switch (contractStatus) {
      case 'uploaded':
        return {
          message: 'Document uploaded. Click "Process Document" to extract structure.',
          type: 'info',
        };
      case 'processing':
        return {
          message: 'Document is being processed. This may take a few minutes.',
          type: 'info',
        };
      case 'error':
        return {
          message: 'Processing failed. You can retry or upload a new version.',
          type: 'warning',
        };
      case 'structured':
      case 'embedded':
      case 'linked':
        return {
          message: 'Document partially processed. More features unlock when fully ready.',
          type: 'info',
        };
      default:
        return null;
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="bg-white rounded-lg shadow-md p-6" data-testid="contract-actions">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>

      {/* Status message */}
      {statusMessage && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-start gap-2 ${
            statusMessage.type === 'warning'
              ? 'bg-warning-50 text-warning-800 border border-warning-200'
              : 'bg-blue-50 text-blue-800 border border-blue-200'
          }`}
          data-testid="action-status-message"
        >
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm">{statusMessage.message}</p>
        </div>
      )}

      {/* Main actions */}
      <div className="flex flex-wrap gap-3">
        {/* Process Document - Primary action when available */}
        {canProcess && (
          <ActionButton
            icon={<PlayCircle className="h-4 w-4" />}
            label={isProcessing ? 'Processing...' : 'Process Document'}
            onClick={onProcess}
            disabled={isProcessing}
            variant="primary"
            isLoading={isProcessing}
            testId="action-process"
          />
        )}

        {/* Processing indicator when in progress */}
        {contractStatus === 'processing' && (
          <div
            className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-50 text-blue-700"
            data-testid="processing-indicator"
          >
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            Processing Document...
          </div>
        )}

        {/* Run Analysis */}
        {canAnalyze && (
          <ActionButton
            icon={<FileSearch className="h-4 w-4" />}
            label={isAnalyzing ? 'Analyzing...' : 'Run Analysis'}
            onClick={onAnalyze}
            disabled={isAnalyzing}
            variant={contractStatus === 'ready' ? 'primary' : 'secondary'}
            isLoading={isAnalyzing}
            testId="action-analyze"
          />
        )}

        {/* Compare */}
        {canCompare && (
          <ActionButton
            icon={<GitCompare className="h-4 w-4" />}
            label="Compare"
            onClick={onCompare}
            variant="secondary"
            testId="action-compare"
          />
        )}

        {/* Export - Only when ready */}
        {canExport && (
          <ActionButton
            icon={<Download className="h-4 w-4" />}
            label="Export"
            onClick={onExport}
            variant="secondary"
            testId="action-export"
          />
        )}

        {/* Refresh button */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Refresh"
            data-testid="action-refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        )}

        {/* More actions dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowMore(!showMore)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="More actions"
            data-testid="action-more"
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>

          {showMore && (
            <div
              className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
              data-testid="more-actions-menu"
            >
              <div className="py-1">
                <button
                  onClick={() => {
                    onExport?.();
                    setShowMore(false);
                  }}
                  disabled={!canExport}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:text-gray-400 disabled:hover:bg-white"
                  data-testid="more-action-export-pdf"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export as PDF
                </button>
                <button
                  onClick={() => {
                    onExport?.();
                    setShowMore(false);
                  }}
                  disabled={!canExport}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:text-gray-400 disabled:hover:bg-white"
                  data-testid="more-action-export-docx"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export as DOCX
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick info about available actions based on status */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Available actions depend on the document processing status.
          {contractStatus !== 'ready' && (
            <> Complete processing to unlock all features.</>
          )}
        </p>
      </div>
    </div>
  );
};
